import { Link } from "wouter";
import { Mail, Phone, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faXTwitter,
  faInstagram,
  faYoutube,
  faLinkedin,
  faXing,
  faWeixin,
  faSnapchatGhost,
  faTiktok
} from "@fortawesome/free-brands-svg-icons";
import { useLanguage } from "@/lib/LanguageContext";

// Define specific type for social links
interface SocialLink {
  icon: any; // Using 'any' as a workaround for the type error
  href: string;
  color: string;
  isCustomIcon?: boolean;
}

const socialLinks: SocialLink[] = [
  {
    icon: faXTwitter,
    href: "https://x.com/Ecoplacetours",
    color: "bg-grey-500 hover:bg-grey-600",
  },
  {
    icon: faFacebookF,
    href: "https://www.facebook.com/people/Ecoplace-Tours/100093548723777/",
    color: "bg-blue-600 hover:bg-blue-700",
  },
  {
      icon: faInstagram,
      href: "https://www.instagram.com/ecoplacetours/",
      color: "bg-gradient-to-r from-[#feda75] via-[#fa7e1e] to-[#d62976] hover:from-[#e1c864] hover:via-[#e17319] hover:to-[#b3235c]",
  },

  {
    icon: faYoutube,
    href: "https://www.youtube.com/channel/UC5WNGtT7ykkIEDr0ZmU8IHQ",
    color: "bg-red-600 hover:bg-red-700",
  },
  {
    icon: faTiktok,
    href: "https://www.tiktok.com/@ecoplacetours?_t=ZM-8v3dWz6ILri&_r=1",
    color: "bg-gray-600 hover:bg-gray-700",
  },
  {
      icon: faSnapchatGhost,
      href: "https://www.snapchat.com/add/ecoplacetours?share_id=wqArHhRdZ3o&locale=en-UG",
      color: "bg-[#FFEC00] hover:bg-[#E5D800]",
  },
  {
    icon: faLinkedin,
    href: "https://www.linkedin.com/in/ecoplace-tours-a3b5692a3/",
    color: "bg-blue-600 hover:bg-blue-700",
  },
  {
    icon: "/rednote2.png",
    href: "https://www.xiaohongshu.com/user/profile/67e137c90000000006013d26?xsec_token=YBWWytSNe8Ns7p-UKsscDwwk4ydCwGc4wl8VD_qFX4e6M%3D&xsec_source=app_share&xhsshare=CopyLink&appuid=67e137c90000000006013d26&apptime=1743157527&share_id=3c78fc67eaa041398399314e2d7d83f8&share_channel=copy_link",
    color: "bg-red-600 hover:bg-red-700",
    isCustomIcon: true,
  },
  /* Commented out for now
  {
    icon: faWeixin,
    href: "https://weixin.qq.com/",
    color: "bg-green-600 hover:bg-green-700",
  }
  */
];

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white py-10 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-4 lg:gap-8"
        >
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center md:text-left mb-6 md:mb-0"
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-5 bg-gradient-to-r from-green-400 to-primary bg-clip-text text-transparent">
              Ecoplace Tours
            </h3>
            <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
              {t("footerTagline")}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-3 sm:gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 sm:w-10 sm:h-10 ${social.color} rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg p-2 touch-manipulation`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                    {social.isCustomIcon ? (
                    <img
                      src={social.icon as string}
                      alt="Social Icon"
                      className="w-3 h-3 sm:w-5 sm:h-5"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={social.icon}
                      className="w-3 h-3 sm:w-5 sm:h-5"
                    />
                  )}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="space-y-2 mb-10 md:mb-0"
          >
            <h4 className="font-semibold text-base sm:text-lg mb-3 sm:mb-5 text-center bg-gradient-to-r from-green-400 to-primary bg-clip-text text-transparent">
              {t("quickLinks")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-2">
              {[
                { text: t("home"), href: "/" },
                { text: t("exploreUganda"), href: "/explore-uganda" },
                { text: t("destinations"), href: "/destinations" },
                { text: t("services"), href: "/services" },
                { text: t("travelInfo"), href: "/travel-info" },
                { text: t("blog"), href: "/blog" },
                { text: t("booking"), href: "/booking" },
                { text: t("aboutUs"), href: "/about" },
              ].map((link, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                  viewport={{ once: true }}
                >
                  <Link
                    href={link.href}
                    className="block text-gray-400 hover:text-white transition-colors duration-200 py-1.5 px-2 sm:py-1 sm:px-2 rounded-md hover:bg-gray-800 text-sm sm:text-base text-center whitespace-nowrap"
                  >
                    {link.text}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <h4 className="font-semibold text-base sm:text-lg mb-3 sm:mb-5 text-center md:text-left bg-gradient-to-r from-green-400 to-primary bg-clip-text text-transparent">
              {t("contactUs")}
            </h4>
            <div className="space-y-3 text-gray-400">
              {[
                {
                  icon: Phone,
                  text: "+256393877773",
                  href: "tel:+256393877773",
                },
                {
                  icon: Mail,
                  text: "info@ecoplacetours.com",
                  href: "mailto:info@ecoplacetours.com",
                },
                {
                  icon: MapPin,
                  text: "Portal Avenue, Kampala, Uganda.",
                  href: "https://maps.google.com/?q=Portal+Avenue,+Kampala,+Uganda",
                },
              ].map((contact, index) => (
                <motion.a
                  key={index}
                  href={contact.href}
                  className="flex items-center p-1.5 hover:text-white transition-colors duration-200 justify-center md:justify-start"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                  viewport={{ once: true }}
                >
                  <contact.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                  <span className="text-xs sm:text-sm truncate">
                    {contact.text}
                  </span>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="border-t border-gray-800 mt-10 pt-8 text-center text-gray-400 text-xs sm:text-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <p>
            &copy; {new Date().getFullYear()}{" "}
            <span className="bg-gradient-to-r from-green-400 to-primary bg-clip-text text-transparent font-medium">
              EcoPlace Tours
            </span>
            . {t("allRightsReserved")}
          </p>
        </motion.div>
      </div>
    </footer>
  );
}
